/**
 * Endereço estruturado, usado nos formulários (ADR 0029). No Brasil, o CEP preenche logradouro,
 * bairro, cidade e UF; número e complemento são digitados. Endereços de outros países usam os
 * mesmos campos (linha, complemento, cidade, região e código postal). A origem fica registrada.
 */
import { isKnownCountry } from './partners.ts';

export const ADDRESS_SOURCES = ['manual', 'postal_code', 'partner', 'contract'] as const;
export type AddressSource = (typeof ADDRESS_SOURCES)[number];

export interface AddressValue {
  country: string;
  postalCode?: string;
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
  city?: string;
  region?: string;
  /** De onde vieram os dados (rastreabilidade): digitado, CEP, cadastro de parceiros... */
  source?: AddressSource;
}

const TEXT_KEYS = ['postalCode', 'street', 'number', 'complement', 'district', 'city', 'region'];
const MAX: Record<string, number> = { postalCode: 20, number: 20, region: 60 };

/** Unidades da federação (UF), para escolher o estado em lista. */
export const BR_STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;

/** CEP só com os 8 números (ou null). */
export function cleanCep(value: string | null | undefined): string | null {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length === 8 ? digits : null;
}

/** 01310100 → 01310-100. */
export function formatCep(value: string): string {
  const d = value.replace(/\D/g, '');
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : value;
}

/** Só o que a consulta de CEP trouxe com conteúdo: o que ela não sabe fica como a pessoa digitou. */
export function cepFill(found: Partial<AddressValue>): Partial<AddressValue> {
  const filled: Partial<AddressValue> = {};
  for (const key of ['postalCode', 'street', 'district', 'city', 'region'] as const) {
    if (found[key]) filled[key] = found[key];
  }
  return filled;
}

/**
 * Confere um endereço preenchido: país válido; texto dentro do limite; no Brasil, CEP com 8
 * números e UF com 2 letras; obrigatório pede logradouro e cidade. Devolve a mensagem ou null.
 */
export function addressIssue(value: unknown, required: boolean): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return 'Endereço inválido';
  }
  const v = value as Record<string, unknown>;
  if (typeof v.country !== 'string' || !isKnownCountry(v.country)) return 'Escolha o país';
  for (const key of Object.keys(v)) {
    if (key === 'country') continue;
    if (key === 'source') {
      if (!(ADDRESS_SOURCES as readonly unknown[]).includes(v.source)) return 'Origem inválida';
      continue;
    }
    if (!TEXT_KEYS.includes(key)) return 'Endereço inválido';
    const text = v[key];
    if (typeof text !== 'string' || text.length > (MAX[key] ?? 200)) {
      return `Endereço inválido (${key})`;
    }
  }
  if (v.country === 'BR') {
    if (v.postalCode && !cleanCep(v.postalCode as string)) return 'CEP com 8 números';
    if (v.region && !/^[A-Z]{2}$/.test(v.region as string)) return 'UF com 2 letras (ex.: MG)';
  }
  if (required && (!v.street || !v.city)) return 'Informe pelo menos logradouro e cidade';
  return null;
}

/** Endereço numa linha (para listas e revisão). */
export function addressLine(v: Partial<AddressValue> | null | undefined): string {
  if (!v) return '';
  const first = [v.street, v.number].filter(Boolean).join(', ');
  return [
    first,
    v.complement,
    v.district,
    [v.city, v.region].filter(Boolean).join('/'),
    v.postalCode,
    v.country,
  ]
    .filter(Boolean)
    .join(' · ');
}
